/**
 * Push Notification Templates Grid
 * Displays available templates with filtering by category
 */
import { useState } from 'react';
import { 
  Zap, Clock, Sparkles, Timer, Gift, Flame, Heart, Sun, CloudRain,
  Coffee, Calendar, Megaphone, Edit, Bell, Copy, Radio
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Icon mapping for templates
const TEMPLATE_ICONS = {
  'zap': Zap,
  'clock': Clock,
  'sparkles': Sparkles,
  'timer': Timer,
  'gift': Gift,
  'flame': Flame,
  'heart': Heart,
  'sun': Sun,
  'cloud-rain': CloudRain,
  'coffee': Coffee,
  'calendar': Calendar,
  'megaphone': Megaphone,
  'edit': Edit,
  'bell': Bell
};

// Color mapping for template badges
const TEMPLATE_COLORS = {
  'yellow': 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  'orange': 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  'purple': 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  'red': 'bg-red-500/10 text-red-600 dark:text-red-400',
  'green': 'bg-green-500/10 text-green-600 dark:text-green-400',
  'pink': 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
  'blue': 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  'brown': 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  'teal': 'bg-teal-500/10 text-teal-600 dark:text-teal-400',
  'indigo': 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
  'gray': 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

const CATEGORY_LABELS = {
  'promotions': 'Promotions',
  'engagement': 'Engagement',
  'announcements': 'Announcements',
  'weather': 'Weather-based',
  'daily': 'Daily',
  'custom': 'Custom'
};

export default function TemplatesGrid({ 
  templates = [], 
  onUseForBroadcast,
  onUseForScheduled
}) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [showDialog, setShowDialog] = useState(false);

  const getTemplateIcon = (iconName) => {
    return TEMPLATE_ICONS[iconName] || Bell;
  };

  const getTemplateColor = (colorName) => {
    return TEMPLATE_COLORS[colorName] || TEMPLATE_COLORS['gray'];
  };

  const getFilteredTemplates = () => {
    if (selectedCategory === 'all') return templates;
    return templates.filter(t => t.category === selectedCategory);
  };

  const getTemplatePreview = (template, vars = {}) => {
    const allVars = { ...(template.example_values || {}), ...vars };
    let title = template.title;
    let body = template.body;
    
    for (const [key, value] of Object.entries(allVars)) {
      title = title.replace(`{${key}}`, value);
      body = body.replace(`{${key}}`, value);
    }
    
    return { title, body };
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateVariables(template.example_values || {});
    setShowDialog(true);
  };

  const handleUseForBroadcast = () => {
    const preview = getTemplatePreview(selectedTemplate, templateVariables);
    onUseForBroadcast?.({
      title: preview.title,
      body: preview.body,
      url: selectedTemplate.url || '/loyalty'
    });
    setShowDialog(false);
    setSelectedTemplate(null);
    setTemplateVariables({});
  };

  const handleUseForScheduled = () => {
    const preview = getTemplatePreview(selectedTemplate, templateVariables);
    onUseForScheduled?.({
      title: preview.title,
      body: preview.body,
      url: selectedTemplate.url || '/loyalty'
    });
    setShowDialog(false);
    setSelectedTemplate(null);
    setTemplateVariables({});
  };

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-foreground">Notification Templates</CardTitle>
              <CardDescription>Pre-made templates for common scenarios</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[160px]" data-testid="template-category-filter">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getFilteredTemplates().map((template) => {
              const IconComponent = getTemplateIcon(template.icon);
              const preview = getTemplatePreview(template);
              
              return (
                <div
                  key={template.id}
                  className="group relative p-4 rounded-lg border border-border bg-muted/20 hover:bg-muted/40 hover:border-primary/30 transition-all cursor-pointer"
                  onClick={() => handleSelectTemplate(template)}
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${getTemplateColor(template.color)}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground truncate">{template.name}</h4>
                        {template.is_system && (
                          <Badge variant="outline" className="text-xs shrink-0">System</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {CATEGORY_LABELS[template.category] || template.category}
                      </p>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground line-clamp-1">{preview.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">{preview.body}</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Template Use Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) {
          setSelectedTemplate(null);
          setTemplateVariables({});
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedTemplate && (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground flex items-center gap-2">
                  {(() => {
                    const IconComponent = getTemplateIcon(selectedTemplate.icon);
                    return (
                      <span className={`p-2 rounded-lg ${getTemplateColor(selectedTemplate.color)}`}>
                        <IconComponent className="w-5 h-5" />
                      </span>
                    );
                  })()}
                  {selectedTemplate.name}
                </DialogTitle>
                <DialogDescription>
                  Customize the template variables and send as broadcast or schedule
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Variables */}
                {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Customize Variables</label>
                    {selectedTemplate.variables.map(varName => (
                      <div key={varName}>
                        <label className="text-xs text-muted-foreground capitalize">{varName.replace(/_/g, ' ')}</label>
                        <Input
                          value={templateVariables[varName] || ''}
                          onChange={(e) => setTemplateVariables(v => ({ ...v, [varName]: e.target.value }))}
                          placeholder={selectedTemplate.example_values?.[varName] || ''}
                          className="mt-1"
                          data-testid={`var-${varName}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Preview */}
                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs text-muted-foreground mb-2">Preview</p>
                  <p className="font-semibold text-foreground">
                    {getTemplatePreview(selectedTemplate, templateVariables).title}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {getTemplatePreview(selectedTemplate, templateVariables).body}
                  </p>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleUseForScheduled}
                  className="w-full sm:w-auto"
                  data-testid="use-for-scheduled"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule
                </Button>
                <Button 
                  onClick={handleUseForBroadcast}
                  className="w-full sm:w-auto bg-primary"
                  data-testid="use-for-broadcast"
                >
                  <Radio className="w-4 h-4 mr-2" />
                  Broadcast Now
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
